import React, { useEffect, useRef, useState } from 'react';
import ChatBubble from '../components/common/ChatBubble';
import ChatInput from '../components/common/ChatInput';
import { useLanguage } from '../context/LanguageContext';
import WizardSubmissionService from '../services/wizardSubmissionService';
import DeepseekChatClient from '../services/deepseekChatClient';
import { extractCVText, validateCVFile } from '../services/cvParser';

/**
 * Parse CV text to extract structured information
 * This provides better context to the AI
 */
const parseCVData = (cvText) => {
  const data = {
    rawText: cvText,
    hasEmail: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(cvText),
    hasPhone: /(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3,4}[-.\s]?\d{4}/.test(cvText),
    hasEducation: /education|degree|bachelor|master|phd|diploma|university|college/i.test(cvText),
    hasExperience: /experience|work|employment|job|position|role/i.test(cvText),
    hasSkills: /skills|proficient|expertise|competenc/i.test(cvText),
    hasLanguages: /language|english|german|french|spanish|ielts|toefl|goethe/i.test(cvText),
    extractedSections: extractSections(cvText)
  };
  return data;
};

/**
 * Extract major sections from CV
 */
const extractSections = (text) => {
  const sections = {};
  const lowerText = text.toLowerCase();

  // Try to find common sections
  if (lowerText.includes('education')) {
    const start = lowerText.indexOf('education');
    const end = Math.min(
      lowerText.indexOf('experience', start) !== -1 ? lowerText.indexOf('experience', start) : text.length,
      lowerText.indexOf('work', start) !== -1 ? lowerText.indexOf('work', start) : text.length
    );
    sections.education = text.substring(start, end).trim();
  }

  if (lowerText.includes('experience') || lowerText.includes('work history')) {
    const start = Math.min(
      lowerText.indexOf('experience') !== -1 ? lowerText.indexOf('experience') : text.length,
      lowerText.indexOf('work history') !== -1 ? lowerText.indexOf('work history') : text.length
    );
    const end = Math.min(
      lowerText.indexOf('skills', start) !== -1 ? lowerText.indexOf('skills', start) : text.length,
      lowerText.indexOf('education', start) !== -1 ? lowerText.indexOf('education', start) : text.length
    );
    sections.experience = text.substring(start, end).trim();
  }

  if (lowerText.includes('skills')) {
    const start = lowerText.indexOf('skills');
    sections.skills = text.substring(start, start + 500).trim(); // Get up to 500 chars
  }

  return sections;
};

const ChatDeepseekPage = () => {
  const { t } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [isThinking, setIsThinking] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [cvInfo, setCvInfo] = useState(null); // Store CV data for context persistence
  const scrollRef = useRef(null);

  useEffect(() => {
    const localId = localStorage.getItem('ravana_chat_session_id');
    if (localId) {
      setSessionId(localId);

      // Restore CV info from localStorage if available
      const storedCvInfo = localStorage.getItem('ravana_cv_info');
      if (storedCvInfo) {
        try {
          const parsed = JSON.parse(storedCvInfo);
          setCvInfo(parsed);
          console.log('Restored CV info from localStorage:', parsed);
        } catch (e) {
          console.error('Failed to parse stored CV info:', e);
        }
      }
    } else {
      const newId = crypto.randomUUID();
      localStorage.setItem('ravana_chat_session_id', newId);
      setSessionId(newId);
    }
    setMessages([{
      id: 'initial',
      role: 'assistant',
      content: t?.('consult.chat.initialPrompt', 'Hello! I am your AI immigration consultant. You can type your question or click "Upload CV" below to begin analyzing your profile.') || 'Hello! I am your AI immigration consultant. You can type your question or click "Upload CV" below to begin analyzing your profile.',
      suggestions: [
        { label: '📎 Upload CV', value: '__upload_cv__' },
        { label: '💬 Start Chat', value: 'Hello, I want to know about immigration options' },
        { label: '🇩🇪 Germany Info', value: 'Tell me about Germany immigration' },
        { label: '🇨🇦 Canada Info', value: 'Tell me about Canada immigration' }
      ]
    }]);
  }, [t]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const addMessage = (msg) => setMessages((prev) => [...prev, msg]);

  const handleSuggestionClick = async (suggestion) => {
    const value = typeof suggestion === 'string' ? suggestion : suggestion.value || suggestion.label;

    // Special handling for Upload CV button
    if (value === '__upload_cv__') {
      // Trigger file input click
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) {
        fileInput.click();
      }
      return;
    }

    await handleUserInput(value);
  };

  const handleUserInput = async (text) => {
    const userMsg = { id: `u-${Date.now()}`, role: 'user', content: text };
    addMessage(userMsg);
    setIsThinking(true);
    try {
      if (!sessionId) throw new Error('No chat session id');

      // Include CV info in every message for context persistence
      console.log('Sending message with CV info:', cvInfo ? 'CV data available' : 'No CV data');
      const resp = await DeepseekChatClient.sendMessage(sessionId, text, [], 'en', cvInfo);

      // Clean up any JSON code blocks in the response
      let content = resp?.reply || 'Okay.';
      if (content.includes('```json')) {
        // Extract JSON from code block
        const jsonMatch = content.match(/```json\s*\n?([\s\S]*?)\n?```/);
        if (jsonMatch) {
          try {
            const parsed = JSON.parse(jsonMatch[1]);
            content = parsed.text || content;
          } catch {
            // If parsing fails, remove the code block markers
            content = content.replace(/```json\s*\n?/g, '').replace(/\n?```/g, '');
          }
        }
      }

      const assistantMsg = {
        id: `a-${Date.now()}`,
        role: 'assistant',
        content: content,
        suggestions: Array.isArray(resp?.suggestions) ? resp.suggestions : undefined,
      };
      addMessage(assistantMsg);
      if (resp?.status === 'completed' && resp?.lead?.id) {
        addMessage({ id: `a-${Date.now() + 1}`, role: 'assistant', content: `✅ Enquiry submitted successfully! Reference ID: ${resp.lead.id}\n\nYour application has been saved. Our team will review it and contact you soon. Do you have any questions?` });
      }
    } catch (err) {
      console.error('Chat flow error:', err);
      addMessage({ id: `a-${Date.now()}`, role: 'assistant', content: 'Sorry, something went wrong. Please try again or upload your CV again.' });
    } finally {
      setIsThinking(false);
    }
  };

  const onFileUpload = async (files) => {
    const file = files?.[0];
    if (!file) return;
    setIsThinking(true);
    try {
      // Validate file
      validateCVFile(file, 10);

      addMessage({ id: `a-${Date.now()}`, role: 'assistant', content: 'Reading your CV…' });

      // Extract text from CV
      const cvText = await extractCVText(file);

      if (!cvText || cvText.trim().length < 50) {
        throw new Error('Could not extract enough text from CV. Please ensure the file is not empty or corrupted.');
      }

      addMessage({ id: `a-${Date.now() + 1}`, role: 'assistant', content: 'Analyzing your CV…' });

      // Upload CV for record keeping
      const uploadInfo = await WizardSubmissionService.uploadCV(file, 'unknown');

      if (!sessionId) throw new Error('No chat session id');

      // Parse CV text to extract structured data
      const extractedData = parseCVData(cvText);

      // Store CV info in state for persistent context
      const cvData = {
        url: uploadInfo?.publicUrl,
        fileName: file.name,
        cvText: cvText,
        extractedData: extractedData
      };
      setCvInfo(cvData);

      // Persist to localStorage for page refresh
      localStorage.setItem('ravana_cv_info', JSON.stringify(cvData));
      console.log('Stored CV info to localStorage and state:', cvData);

      // Send CV text to AI for analysis with structured data
      const messageWithCV = `I am uploading my CV for analysis. Here is the content:\n\n${cvText}\n\nPlease analyze my CV and ask me targeted questions ONLY for information that is NOT already in my CV.`;

      const resp = await DeepseekChatClient.sendMessage(
        sessionId,
        messageWithCV,
        [{ url: uploadInfo?.publicUrl, name: file.name, type: file.type || 'application/octet-stream' }],
        'en',
        cvData
      );

      const assistantMsg = {
        id: `a-${Date.now() + 2}`,
        role: 'assistant',
        content: resp?.reply || 'Thanks, I have analyzed your CV. Let me ask you some questions to better understand your immigration goals.',
        suggestions: Array.isArray(resp?.suggestions) ? resp.suggestions : undefined,
      };
      addMessage(assistantMsg);
    } catch (err) {
      console.error('CV upload/analysis error:', err);
      const errorMessage = err.message || 'Failed to analyze CV. Please upload a PDF/DOCX/TXT file under 10MB.';
      addMessage({ id: `a-${Date.now()}`, role: 'assistant', content: errorMessage });
    } finally {
      setIsThinking(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="heading-tight text-3xl font-bold">Immigration Assistant</h1>
        <p className="lede">Chat to find your best migration pathway. This assistant will analyze your CV and match programs for you.</p>
      </div>

      <div ref={scrollRef} className="space-y-4 max-h-[60vh] overflow-y-auto card p-4 section-muted">
        {messages.map((m) => (
          <ChatBubble key={m.id} message={m} onSuggestionClick={handleSuggestionClick} />
        ))}
      </div>

      <ChatInput onSend={handleUserInput} isThinking={isThinking} onFileUpload={onFileUpload} />
    </div>
  );
};

export default ChatDeepseekPage;
